// GET responses saved on the device, so screens still show your plan and the exercise list with no signal.
import { useCallback, useEffect, useState } from 'react';
import type { z } from 'zod';
import { api, errorText, isOffline } from './api.ts';
import { db } from './log/db.ts';

/** Fetches `path`, saving the response; offline, returns the saved copy instead (or rethrows if there isn't one). */
export async function cachedApi<T extends z.ZodType>(userId: string, path: string, schema: T): Promise<{ data: z.infer<T>; saved: boolean }> {
  try {
    const data = await api(path, schema);
    await db.cache.put({ key: path, userId, data, savedAt: Date.now() }).catch(() => {});
    return { data, saved: false };
  } catch (e) {
    if (!isOffline(e)) throw e;
    const hit = await db.cache.get(path).catch(() => undefined);
    if (!hit || hit.userId !== userId) throw e;
    return { data: schema.parse(hit.data), saved: true };
  }
}

/** Like useApi, for a GET that should still work offline. `saved` is true when showing the device's copy. */
export function useCachedApi<T extends z.ZodType>(userId: string, path: string | null, schema: T) {
  const [state, setState] = useState<{ data?: z.infer<T>; error: string | null; saved: boolean }>({ error: null, saved: false });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    cachedApi(userId, path, schema)
      .then(({ data, saved }) => alive && setState({ data, error: null, saved }))
      .catch((e: unknown) => alive && setState((s) => ({ ...s, error: errorText(e) })));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}
